import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import { CustomIcon, CustomTooltip, EIconName, SwitchElement } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './price-per-minute.scss';

// Components
import { PerMinuteItem } from './children/per-minute-item/per-minute-item';
import {
  DeleteCostPerSessionModal
} from '../../../../modals/delete-cost-per-session-modal/delete-cost-per-session-modal';
import { FreeMinutesItem } from './children/free-minutes-item/free-minutes-item';

// Store entities
import {
  updateFreeMinutes,
  updatePricePerMinute
} from '../../../../store/current-counsellor/current-counsellor.thunks';

// Utils
import { numToFixed } from '../../../../utils/math';
import { replaceCurrencySymbol } from '../../../../utils/currency';

// Models
import { ICounsellor, IFreeMinutes } from '../../../../interfaces/counsellors.interfaces';
import { ICurrency } from '../../../../interfaces/company.interfaces';
import { TPPMType } from '../../../../interfaces/price-settings.interfaces';
import { openNotification } from '../../../../store/app-notifications/app-notifications.thunks';

type TFormValue = {
  audio_call_rate: string;
  profit_audio_call_rate: string;
  call_rate: string;
  profit_call_rate: string;
  conversation_rate: string;
  profit_conversation_rate: string;
};

export type TPricePerMinuteRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TPricePerMinuteProps = {
  counsellor: ICounsellor;
  free_minutes: IFreeMinutes;
  percentFee: number;
  transactionFee: number;
  is_stripe_connected: boolean;
  currency: ICurrency;
};

export const PricePerMinute = forwardRef<TPricePerMinuteRef, TPricePerMinuteProps>((
  { counsellor, free_minutes, percentFee, transactionFee, is_stripe_connected, currency }: TPricePerMinuteProps, ref
) => {
  const [form] = Form.useForm<TFormValue>();
  const audioCallRate = Form.useWatch('audio_call_rate', { form, preserve: true });
  const audioProfitRate = Form.useWatch('profit_audio_call_rate', { form, preserve: true });
  const videoRate = Form.useWatch('call_rate', { form, preserve: true });
  const videoProfitRate = Form.useWatch('profit_call_rate', { form, preserve: true });
  const conversationRate = Form.useWatch('conversation_rate', { form, preserve: true });
  const conversationProfitRate = Form.useWatch('profit_conversation_rate', { form, preserve: true });

  const [savingChanges, setSavingChanges] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  const [hasChanges, setHasChanges] = useState<{
    call_rate: boolean;
    audio_call_rate: boolean;
    conversation_rate: boolean;
  }>({ call_rate: false, audio_call_rate: false, conversation_rate: false });

  const [availableRates, setAvailableRates] = useState<{
    call_rate: boolean;
    audio_call_rate: boolean;
    conversation_rate: boolean;
  }>({
    call_rate: counsellor.is_video_call_enabled,
    audio_call_rate: counsellor.is_audio_call_enabled,
    conversation_rate: counsellor.is_conversation_enabled
  });

  const dispatch = useDispatch<any>();

  const getValueByProfit = (profitValue: number): number => {
    return numToFixed(profitValue / (1 - (percentFee / 100)));
  }

  const getValueByRate = (currValue: number): number => {
    // return numToFixed(currValue / (1 + (percentFee / 100)));
    return numToFixed(currValue - getServiceFeeValue(currValue));
  }

  const getProfitInitialValue = (currValue: number): string => {
    const fee = percentFee / 100;
    if (currValue * fee <= 0) return `${ currency.symbol }0`;
    return numToFixed(getValueByRate(currValue)).toString();
  }

  const getServiceFeeValue = (rateValue: number): number => {
    const fee = percentFee / 100;
    if (rateValue * fee <= 0 || numToFixed(rateValue * fee) <= 0) return 0.01;
    return numToFixed(rateValue * fee);
  }

  const getNumberField = (value: string | number): number => {
    return Number.parseFloat(replaceCurrencySymbol(`${ value }`, currency.symbol));
  }

  const audioRateInitialValue = `${ currency.symbol }${ counsellor.audio_call_rate.value || 0.01 }`;
  const audioProfitInitialValue = getProfitInitialValue(counsellor.audio_call_rate.value);
  const audioServiceFee = useMemo(() => getServiceFeeValue(getNumberField(audioCallRate)), [audioCallRate]);
  const audioNumberRate = useMemo(() => getNumberField(audioCallRate), [audioCallRate]);
  const audioNumberProfit = useMemo(() => getNumberField(audioProfitRate), [audioProfitRate]);

  const videoRateInitialValue = `${ currency.symbol }${ counsellor.call_rate.value || 0.01 }`;
  const videoProfitInitialValue = getProfitInitialValue(counsellor.call_rate.value);
  const videoServiceFee = useMemo(() => getServiceFeeValue(getNumberField(videoRate)), [videoRate]);
  const videoNumberRate = useMemo(() => getNumberField(videoRate), [videoRate]);
  const videoNumberProfit = useMemo(() => getNumberField(videoProfitRate), [videoProfitRate]);

  const conversationRateInitialValue = `${ currency.symbol }${ counsellor.conversation_rate.value || 0.01 }`;
  const conversationProfitInitialValue = getProfitInitialValue(counsellor.conversation_rate.value);
  const conversationServiceFee = useMemo(() => getServiceFeeValue(getNumberField(conversationRate)), [conversationRate]);
  const conversationNumberRate = useMemo(() => getNumberField(conversationRate), [conversationRate]);
  const conversationNumberProfit = useMemo(() => getNumberField(conversationProfitRate), [conversationProfitRate]);

  const toggleRates = async (type: TPPMType) => {
    setSavingChanges(true);
    const payload = await dispatch(updatePricePerMinute({
      worker_id: counsellor.id,
      ...(type === 'call_rate' && {
        is_call_rate_enabled: Number(!counsellor.is_video_call_enabled),
        is_audio_call_rate_enabled: Number(counsellor.is_audio_call_enabled),
        is_conversation_rate_enabled: Number(counsellor.is_conversation_enabled),
      }),
      ...(type === 'audio_call_rate' && {
        is_call_rate_enabled: Number(counsellor.is_video_call_enabled),
        is_audio_call_rate_enabled: Number(!counsellor.is_audio_call_enabled),
        is_conversation_rate_enabled: Number(counsellor.is_conversation_enabled),
      }),
      ...(type === 'conversation_rate' && {
        is_call_rate_enabled: Number(counsellor.is_video_call_enabled),
        is_audio_call_rate_enabled: Number(counsellor.is_audio_call_enabled),
        is_conversation_rate_enabled: Number(!counsellor.is_conversation_enabled),
      }),
    }));

    setSavingChanges(false);

    if (payload) {
      setHasChanges({ ...hasChanges, [type]: false });
    }
  };

  const toggleEditMode = (type: TPPMType, checked: boolean, event: any) => {
    if (!checked && counsellor.rates_enabled_count === 1) {
      setDeleteOpen(true);
      event.preventDefault();
      return;
    }
    if (counsellor.is_messages_enabled && hasChanges[type]) {
      form.setFieldsValue({
        ...(type === 'call_rate' && { call_rate: videoRateInitialValue }),
        ...(type === 'audio_call_rate' && { audio_call_rate: audioRateInitialValue }),
        ...(type === 'conversation_rate' && { conversation_rate: conversationRateInitialValue }),
        [`profit_${ type }`]: '0'
      });
    }

    toggleRates(type);

    setAvailableRates({
      ...availableRates,
      [type]: !availableRates[type]
    });
  };

  const lastRatesDeleteHandler = (state: boolean): void => {
    setDeleteOpen(false);
    if (!state) return;

    const lastType = Object.keys(availableRates).find((key) => availableRates[key as keyof typeof availableRates]);

    if (!lastType) return;

    toggleRates(lastType as TPPMType);

    setAvailableRates({
      ...availableRates,
      [lastType as TPPMType]: !availableRates[lastType as TPPMType]
    });
  }

  const handleRateChange = (
    currentValue: string | number,
    profitField: keyof TFormValue,
    currentField: keyof TFormValue,
    type: TPPMType
  ): void => {
    const profitValue = currentField.includes('profit') ? getValueByProfit(+currentValue) : getValueByRate(+currentValue);
    const savedValue = getNumberField(counsellor[type].value);

    const hasChanges =
      currentField.includes('profit') ? savedValue !== numToFixed(profitValue) :
        savedValue !== numToFixed(profitValue) + getServiceFeeValue(savedValue);

    form.setFieldValue(profitField, (!profitValue || profitValue < 0) ? 0 : numToFixed(profitValue));
    setHasChanges(prevState => ({ ...prevState, [type]: hasChanges }));
  };

  const onValuesChange = (currentValue: { [key in keyof TFormValue]: string | number }, _: TFormValue): void => {
    if (currentValue?.hasOwnProperty('call_rate')) {
      handleRateChange(currentValue?.call_rate, 'profit_call_rate', 'call_rate', 'call_rate');
    }

    if (currentValue?.hasOwnProperty('profit_call_rate')) {
      handleRateChange(currentValue?.profit_call_rate, 'call_rate', 'profit_call_rate', 'call_rate');
    }

    if (currentValue?.hasOwnProperty('audio_call_rate')) {
      handleRateChange(currentValue?.audio_call_rate, 'profit_audio_call_rate', 'audio_call_rate', 'audio_call_rate');
    }

    if (currentValue?.hasOwnProperty('profit_audio_call_rate')) {
      handleRateChange(currentValue?.profit_audio_call_rate, 'audio_call_rate', 'profit_audio_call_rate', 'audio_call_rate');
    }

    if (currentValue?.hasOwnProperty('conversation_rate')) {
      handleRateChange(currentValue?.conversation_rate, 'profit_conversation_rate', 'conversation_rate', 'conversation_rate');
    }

    if (currentValue?.hasOwnProperty('profit_conversation_rate')) {
      handleRateChange(currentValue?.profit_conversation_rate, 'conversation_rate', 'profit_conversation_rate', 'conversation_rate');
    }
  };

  const saveChanges = async (type: TPPMType) => {
    const key = `profit_${ type }`;
    const rateKey = type;
    const value = getNumberField(form.getFieldsValue()[key as keyof TFormValue]);

    setSavingChanges(true);

    const { payload } = await dispatch(updatePricePerMinute({
      worker_id: counsellor.id,
      is_call_rate_enabled: Number(counsellor.is_video_call_enabled),
      is_audio_call_rate_enabled: Number(counsellor.is_audio_call_enabled),
      is_conversation_rate_enabled: Number(counsellor.is_conversation_enabled),
      [rateKey]: Number.parseFloat(replaceCurrencySymbol(`${ value }`, currency.symbol)) || 0
    }));

    setSavingChanges(false);

    if (!payload) return;

    setHasChanges({
      ...hasChanges, [type]: false
    });
  };

  const toggleFullFreeServicesHandler = (full_free_service_enabled: boolean, event: any): void => {
    if (!is_stripe_connected && !full_free_service_enabled) {
      event.preventDefault();
      dispatch(openNotification({
        type: 'error',
        description: 'Please connect your Stripe account first to deactivate the free services'
      }));
      return;
    }

    dispatch(updateFreeMinutes({
      id: counsellor.id,
      free_minutes: {
        ...free_minutes,
        full_free_service_enabled
      }
    }));
  }
  const toggleFreeMinutesHandler = (free_minutes_enabled: boolean, event: any): void => {
    dispatch(updateFreeMinutes({
      id: counsellor.id,
      free_minutes: {
        ...free_minutes,
        free_minutes_enabled,
      }
    }));
  }

  const saveFreeMinutesHandler = async (free_service_minutes: number, prepaid_enabled: boolean): Promise<{ payload: any }> => {
    return dispatch(updateFreeMinutes({
      id: counsellor.id,
      free_minutes: {
        ...free_minutes,
        prepaid_enabled,
        free_service_minutes
      }
    }));
  }

  useImperativeHandle(ref, () => ({
    isHasChanges(): boolean {
      return hasChanges.call_rate || hasChanges.audio_call_rate || hasChanges.conversation_rate;
    },
    resetState(): void {
      form.setFieldsValue({
        profit_call_rate: videoProfitInitialValue,
        profit_audio_call_rate: audioProfitInitialValue,
        profit_conversation_rate: conversationProfitInitialValue,
        audio_call_rate: replaceCurrencySymbol(audioRateInitialValue, currency.symbol),
        call_rate: replaceCurrencySymbol(videoRateInitialValue, currency.symbol),
        conversation_rate: replaceCurrencySymbol(conversationRateInitialValue, currency.symbol)
      });
      setHasChanges({
        call_rate: false,
        audio_call_rate: false,
        conversation_rate: false,
      });
      setSavingChanges(false);
    }
  }));

  useEffect(() => {
    setAvailableRates({
      audio_call_rate: counsellor.is_audio_call_enabled,
      call_rate: counsellor.is_video_call_enabled,
      conversation_rate: counsellor.is_conversation_enabled,
    });
  }, [counsellor]);

  return (
    <div className={ `price-per-minute ${ free_minutes.full_free_service_enabled ? 'disabled-block' : '' }` }>

      <DeleteCostPerSessionModal open={ deleteOpen }
                                 isLastItem={ counsellor?.rates_enabled_count === 1 }
                                 title={ 'Are you sure you want to delete Cost per Minute?' }
                                 onClose={ lastRatesDeleteHandler }/>

      {/*<div className="price-per-minute--free-services">*/ }
      {/*  <div className="free-services-action">*/ }
      {/*    <SwitchElement checked={ free_minutes.full_free_service_enabled } onChange={ toggleFullFreeServicesHandler }/>*/ }
      {/*  </div>*/ }
      {/*  <div className={ `free-services-title ${ !free_minutes.full_free_service_enabled ? 'disabled-block' : '' }` }>*/ }
      {/*    Provide Services for FREE <CustomTooltip title={ 'Provide services for free' }>*/ }
      {/*    <CustomIcon size={ 'size-md' } name={ EIconName.Info }/>*/ }
      {/*  </CustomTooltip>*/ }
      {/*  </div>*/ }
      {/*</div>*/ }

      <div
        className={ `price-per-minute--description` }>
        In this section, you can set the price for Instant Video, Audio calls and Text Chats. To enable Instant Pay per
        Minute Calls, activate the audio, video, text
        option separately using the provided switchers. The switcher's off-state indicates that the consultation type
        is not available in the Consultant's profile for Clients.
      </div>

      <div
        className={ `price-per-minute--description` }>
        When setting Service Prices, consider the service charges you wish to receive as a business. The system assists
        you by showing the Credit Card Processing Fee in relation to your set service prices. No hidden or extra
        charges. This enables transparency in pricing for you.
      </div>

      <div
        className={ `price-per-minute--description` }>
        <FreeMinutesItem free_minutes={ free_minutes }
                         toggleFreeMode={ toggleFreeMinutesHandler }
                         saveChanges={ saveFreeMinutesHandler }
        />
      </div>

      <div
        className={ `price-per-minute--notification` }>
        <CustomIcon name={ EIconName.Info } skipSize={ true }/> Each Minute-Based Call is subject to a
        fixed { currency.symbol }{ transactionFee } { currency.name } transaction fee charged by the Payment System,
        regardless of call length. This fee is not included in the Payable Amount for Clients per min.
      </div>

      <Form form={ form } onValuesChange={ onValuesChange }
            className={ `per-minutes-wrapper` }>
        <PerMinuteItem type={ 'call_rate' }
                       currency={ currency }
                       initialRate={ videoRateInitialValue }
                       initialProfit={ videoProfitInitialValue }
                       hourRate={ videoNumberRate }
                       hourProfit={ videoNumberProfit }
                       serviceFee={ videoServiceFee }
                       serviceEnabled={ counsellor.is_video_call_enabled }
                       savingChanges={ savingChanges }
                       hasChanges={ hasChanges.call_rate }
                       availableRates={ availableRates.call_rate }
                       toggleEditMode={ toggleEditMode }
                       saveChanges={ saveChanges }
                       disabledBlock={ free_minutes.full_free_service_enabled }
        />

        <PerMinuteItem type={ 'audio_call_rate' }
                       currency={ currency }
                       initialRate={ audioRateInitialValue }
                       initialProfit={ audioProfitInitialValue }
                       hourRate={ audioNumberRate }
                       hourProfit={ audioNumberProfit }
                       serviceFee={ audioServiceFee }
                       serviceEnabled={ counsellor.is_audio_call_enabled }
                       savingChanges={ savingChanges }
                       hasChanges={ hasChanges.audio_call_rate }
                       availableRates={ availableRates.audio_call_rate }
                       toggleEditMode={ toggleEditMode }
                       saveChanges={ saveChanges }
                       disabledBlock={ free_minutes.full_free_service_enabled }
        />

        <PerMinuteItem type={ 'conversation_rate' }
                       currency={ currency }
                       initialRate={ conversationRateInitialValue }
                       initialProfit={ conversationProfitInitialValue }
                       hourRate={ conversationNumberRate }
                       hourProfit={ conversationNumberProfit }
                       serviceFee={ conversationServiceFee }
                       serviceEnabled={ counsellor.is_conversation_enabled }
                       savingChanges={ savingChanges }
                       hasChanges={ hasChanges.conversation_rate }
                       availableRates={ availableRates.conversation_rate }
                       toggleEditMode={ toggleEditMode }
                       saveChanges={ saveChanges }
                       disabledBlock={ free_minutes.full_free_service_enabled }
        />
      </Form>
    </div>
  );
})
